import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import styles from './Theoretical.module.scss';
import { setSurveyQuestionIndex, getLeiModalOpen, setLeiModalOpen, setIsChangingQuestion, ISDANettingEngineData, getIsChangingQuestion, NettingEngineData, GMRANettingEngineData, setNettingEngineAgreementType, getNettingEngineAgreementType } from '../store';
import { classifySoftSearchEntityStarted, Lei, searchEntitiesStarted } from '../../../admin/entity/store';
import { SearchEntityModal } from '../../../admin/entity/SearchEntityModal';
import { TheoreticalSurveyISDA } from './TheoreticalSurveyISDA';
import { TheoreticalSurveyGMRA } from './TheoreticalSurveyGMRA';
import { AgreementType } from '../../../admin/documents/store/types';

interface TheoreticalSurveyProps {
    surveyQuestionIndex: number;
    surveyAnswers: NettingEngineData | null;
}

export const TheoreticalSurvey: React.FC<TheoreticalSurveyProps> = ({
    surveyQuestionIndex,
    surveyAnswers
}) => {
    const dispatch = useAppDispatch();
    const changingQuestion = useAppSelector(getIsChangingQuestion);
    const agreementType = useAppSelector(getNettingEngineAgreementType);
    const [selectedEntity, setSelectedEntity] = useState<Lei | null>(null);

    const setQuestionIndex = useCallback((index: number) => dispatch(setSurveyQuestionIndex(index)), [dispatch]);
    const agreementTypeDropdownOptions: DropdownOption[] = useMemo(() => [{ label: 'ISDA', value: AgreementType.ISDA.toString() }, { label: 'GMRA', value: AgreementType.GMRA.toString() }], []);
    const getDropdownValueForAnswer = useCallback((answer: AgreementType | null) => isNull(answer) ? null : agreementTypeDropdownOptions.find(({ value }) => parseInt(value) === answer)!, [agreementTypeDropdownOptions]);
    const agreementTypeSelected = useMemo(() => !isNull(agreementType), [agreementType]);
    const isISDANettingEngine = useMemo(() => agreementTypeSelected && agreementType === AgreementType.ISDA, [agreementTypeSelected, agreementType]);

    const leiModalOpen = useAppSelector(getLeiModalOpen);
    const closeEntityModal = useCallback(() => dispatch(setLeiModalOpen(false)), [dispatch]);
    const searchEntities = () => dispatch(searchEntitiesStarted());

    const softSaveEntity = useCallback((entity: Lei) => {
        setSelectedEntity(entity);
        dispatch(classifySoftSearchEntityStarted({
            name: entity.legalName,
            lei: entity.lei,
            city: entity.legalAddress.city,
            country: entity.legalAddress.country,
            jurisdiction: entity.jurisdiction
        }));
        setTimeout(() => {
            setQuestionIndex(surveyQuestionIndex + 1);
            dispatch(setIsChangingQuestion(false));
        }, 750);
    }, [dispatch, setQuestionIndex, surveyQuestionIndex]);

    const updateNettingEngineAgreementType = useCallback((option: DropdownOption) => dispatch(setNettingEngineAgreementType(parseInt(option.value) as AgreementType)), [dispatch]);

    const theoryToShow = useMemo(() => isISDANettingEngine ?
        <TheoreticalSurveyISDA
            surveyQuestionIndex={surveyQuestionIndex}
            surveyAnswers={surveyAnswers as ISDANettingEngineData}
            selectedEntity={selectedEntity}
        /> :
        <TheoreticalSurveyGMRA
            surveyQuestionIndex={surveyQuestionIndex}
            surveyAnswers={surveyAnswers as GMRANettingEngineData}
            selectedEntity={selectedEntity}
        />, [isISDANettingEngine, surveyQuestionIndex, surveyAnswers, selectedEntity]);

    return (
        <div className={styles.survey}>
            {!agreementTypeSelected &&
                <div className={styles.surveyWrapper}>
                    <div className={styles.surveyQuestionsWrapper}>
                        <div className={classNames(styles.surveyAgreementQuestion, { [styles.surveyQuestionFadeOut]: changingQuestion })}>Which type of agreement do you intend to put in place with your counterparty?</div>
                        <div className={styles.surveyInput}>
                            <div className={styles.surveyDropdownWrapper}>
                                <Dropdown
                                    value={(getDropdownValueForAnswer(agreementType))}
                                    options={agreementTypeDropdownOptions!}
                                    onChange={value => updateNettingEngineAgreementType(value as DropdownOption)}
                                    closeOnSelect
                                />
                            </div>
                        </div>
                    </div>
                </div>
            }
            {agreementTypeSelected && theoryToShow}
            <SearchEntityModal
                searchEntities={searchEntities}
                closeModal={closeEntityModal}
                isOpen={leiModalOpen}
                softSaveEntity={softSaveEntity}
                selectedEntity={selectedEntity}
            />
        </div>
    );
};
